import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Grid,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Chip,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useReactToPrint } from "react-to-print";
import http from "../../../commonFunctions/httpClient";
import WSButton from "../../../Components/Button/WSButton";
import logo from "../../../images/logo_invoice.svg";

// ✅ INTERFACES for LabBooking API (matches your API response)
interface LabUser {
  userId: string;
  name: string;
  gender: string;
  age: number;
  profileImage?: string;
  status: string | null;
  bookingDate: string;
}

interface LabTest {
  userId: string;
  userName: string;
  testName: string;
}

interface LabBookingInfo {
  bookingId: string;
  bookedOn: string;
  lab: {
    labId: string;
    labName: string;
    contact: string | null;
    email: string | null;
  };
  payment: {
    method: string;
    amount: number;
  };
  tests: LabTest[];
  slot: {
    date: string;
    time: string;
  };
  collection: {
    type: string;
    address: {
      line1: string;
      landmark: string;
      city: string | null;
      district: string | null;
      pincode: string;
    };
  };
}

interface OrderTracking {
  orderId: string;
  trackingId: string | null;
  steps: Array<{ status: string }>;
}

interface LabResult {
  userId: string;
  userName: string;
  resultAvailable: boolean;
  reportPdfUrl: string | null;
}

interface OrderSummary {
  itemTotal: {
    quantity: number;
    pricePerItem: number;
    total: number;
  };
  discount: number;
  homeCollectionFee: number;
  serviceFeeAndTax: number;
  totalPayable: number;
  savings: number;
}

interface LabBookingResponse {
  success: boolean;
  message: string;
  data: {
    users: LabUser[];
    testBookingInfo: LabBookingInfo[];
    results: LabResult[];
    orderSummary: OrderSummary[];
    orderTracking: OrderTracking[];
  };
}

// ✅ FIXED PROPS INTERFACE - Accepts bookingId (matches Revenue.tsx usage)
interface LabInvoiceReportProps {
  bookingId: string;
}

export const LabInvoiceReport: React.FC<LabInvoiceReportProps> = ({ bookingId }) => {
  const [labData, setLabData] = useState<LabBookingResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const contentRef = useRef<HTMLDivElement>(null);

  const fetchLabData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      
      console.log("🔄 Fetching lab booking for bookingId:", bookingId);
      
      const response: LabBookingResponse = await http.get(
        `lab/LabBooking/details/${bookingId}`  // ✅ YOUR LAB API
      );
      
      console.log("📊 FULL LAB BOOKING API RESPONSE:", response);
      
      if (response.success) {
        setLabData(response.data);
      } else {
        setError("No valid lab booking found for this booking ID");
        setLabData(null);
      }
      
    } catch (error: any) {
      console.error("❌ LAB BOOKING API ERROR:", error);
      setError("Failed to fetch lab booking data");
      setLabData(null);
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    if (bookingId) {
      fetchLabData();
    }
  }, [fetchLabData]);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const formatTimeSlot = (timeSlot: string) => {
    return timeSlot.replace(/[:-]/g, ' - ');
  };

  const formatCurrency = (amount: number) => {
    return `₹${amount.toLocaleString("en-IN")}`;
  };

  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading lab invoice...</Typography>
      </Box>
    );
  }

  if (error || !labData) {
    return (
      <Box p={4}>
        <Alert severity="error">
          {error || "No lab booking data found for this booking ID"}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Booking ID: {bookingId}
            </Typography>
          </Box>
        </Alert>
      </Box>
    );
  }

  const firstBooking = labData.testBookingInfo[0] || {};
  const firstUser = labData.users[0] || { name: "Unknown" };
  const firstResult = labData.results[0] || { resultAvailable: false };
  const firstSummary = labData.orderSummary[0] || { 
    itemTotal: { quantity: 0, pricePerItem: 0, total: 0 },
    discount: 0, 
    homeCollectionFee: 0, 
    serviceFeeAndTax: 0, 
    totalPayable: 0, 
    savings: 0 
  };
  const firstTracking = labData.orderTracking[0] || { trackingId: null };

  return (
    <Box p={4} sx={{ backgroundColor: "#fff", maxWidth: "100%" }}>
      <Box sx={{ mb: 3, textAlign: "center" }}>
        <WSButton
          id="printLabInvoiceBtn"
          size="small"
          label={`Print Lab Invoice #${bookingId.slice(-6).toUpperCase()}`}
          onClick={handlePrint}
        />
      </Box>

      <Box
        ref={contentRef}
        sx={{ 
          p: 4, 
          width: "100%", 
          maxWidth: "800px", 
          margin: "0 auto",
          bgcolor: "white",
          '@media print': {
            maxWidth: "none",
            width: "100%",
            p: 2,
            margin: 0
          }
        }}
      >
        {/* HEADER */}
        <Grid container sx={{ mb: 3 }}>
          {/* LOGO */}
          <Grid item xs={12} textAlign="left" sx={{ mb: -4 }}>
            <Box
              component="img"
              src={logo}
              alt="MELP Lab Logo"
              sx={{
                width: 100,
                height: 100,
                borderRadius: "12px",
                objectFit: "contain",
                '@media print': {
                  filter: "none",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)"
                }
              }}
              onError={(e: any) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Grid>
          
          {/* COMPANY INFO */}
          <Grid item xs={12}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={6}>
                <Typography fontSize={20} fontWeight="bold" sx={{ color: "#098963", mb: 1 }}>
                  MELP Lab
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                  Diagnostic & Pathology Services
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  123 Health Street, Kerala - 670001
                </Typography>
                <Typography variant="body2">
                  Phone: +91 98765 43210
                </Typography>
              </Grid>
              <Grid item xs={6} sx={{ minHeight: 80 }} />
            </Grid>
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* ORDER DETAILS */}
        <Grid container justifyContent="space-between" mb={3}>
          <Grid item xs={6}>
            <Typography fontSize={18} gutterBottom sx={{ fontWeight: 700 }}>
              Lab Invoice #{bookingId.slice(-6).toUpperCase()}
            </Typography>
            <Typography sx={{ fontWeight: 500, mb: 1 }}>
              <strong>Customer:</strong> {firstUser.name}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Customer ID:</strong> {firstUser.userId?.slice(-8) || 'N/A'}
            </Typography>
            <Typography sx={{ mb: 1 }}>
              <strong>Lab:</strong> {firstBooking.lab?.labName || 'N/A'}
            </Typography>
            <Typography sx={{ mb: 0.5 }}>
              <strong>Collection:</strong> {firstBooking.collection?.type || 'N/A'}
            </Typography>
            {firstBooking.collection?.address && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {firstBooking.collection.address.line1}, {firstBooking.collection.address.landmark}
              </Typography>
            )}
          </Grid>
          <Grid item xs={6} textAlign="right">
            <Typography fontSize={18} sx={{ fontWeight: 700, mb: 2 }}>
              <strong>Date:</strong> {formatDate(firstBooking.bookedOn)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Slot:</strong> {formatDate(firstBooking.slot.date)} {formatTimeSlot(firstBooking.slot.time)}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              <strong>Payment:</strong> {firstBooking.payment?.method || 'N/A'}
            </Typography>
            {firstTracking.trackingId && (
              <Chip
                label={firstTracking.trackingId}
                color="primary"
                sx={{ fontWeight: 600 }}
              />
            )}
          </Grid>
        </Grid>

        {/* TESTS TABLE */}
        <TableContainer sx={{ mb: 4, borderRadius: 2, boxShadow: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#098963" }}>
                <TableCell sx={{ fontWeight: 700, color: 'white' }}>Test Name</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Customer</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="right">Price</TableCell>
                <TableCell sx={{ fontWeight: 700, color: 'white' }} align="center">Qty</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labData.testBookingInfo.length > 0 ? (
                labData.testBookingInfo.map((booking: LabBookingInfo, bookingIndex: number) =>
                  booking.tests.map((test: LabTest, testIndex: number) => (
                    <TableRow key={`${booking.bookingId}-${test.userId}-${testIndex}`} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>
                        <Typography variant="body2">{test.testName}</Typography>
                      </TableCell>
                      <TableCell align="center">{test.userName}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(firstSummary.itemTotal.pricePerItem)}
                      </TableCell>
                      <TableCell align="center">1</TableCell>
                    </TableRow>
                  ))
                )
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tests found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* BILL SUMMARY */}
        <Box sx={{ borderTop: "3px solid #098963", pt: 3, mb: 4 }}>
          <Grid container justifyContent="flex-end">
            <Grid item xs={12} sm={6} md={4}>
              <Box textAlign="right">
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  <strong>Items Total:</strong> {formatCurrency(firstSummary.itemTotal.total)}
                </Typography>
                {firstSummary.discount > 0 && (
                  <Typography variant="h6" color="error" sx={{ fontWeight: 700, mb: 1 }}>
                    <strong>Discount:</strong> -{formatCurrency(firstSummary.discount)}
                  </Typography>
                )}
                {firstSummary.homeCollectionFee > 0 && (
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    <strong>Home Collection:</strong> {formatCurrency(firstSummary.homeCollectionFee)}
                  </Typography>
                )}
                {firstSummary.serviceFeeAndTax > 0 && (
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    <strong>Service Fee + Tax:</strong> {formatCurrency(firstSummary.serviceFeeAndTax)}
                  </Typography>
                )}
                <Divider sx={{ my: 1, borderWidth: 2 }} />
                <Typography fontSize={20} color="#098963" sx={{ fontWeight: 900, mb: 1 }}>
                  <strong>Total Payable:</strong> {formatCurrency(firstSummary.totalPayable)}
                </Typography>
                <Typography variant="body2" color="success.main">
                  <strong>Savings:</strong> {formatCurrency(firstSummary.savings)}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* RESULTS STATUS */}
        {labData.results.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Grid container justifyContent="center">
              <Grid item xs={12} textAlign="center">
                <Typography variant="h6" sx={{ mb: 1, color: labData.results[0].resultAvailable ? "success.main" : "warning.main" }}>
                  {labData.results[0].resultAvailable ? "✅ Lab Report Available" : "⏳ Lab Report Pending"}
                </Typography>
                {labData.results[0].resultAvailable && labData.results[0].reportPdfUrl && (
                  <Typography variant="body2" sx={{ color: "primary.main" }}>
                    Report ready for download
                  </Typography>
                )}
              </Grid>
            </Grid>
          </>
        )}

        {/* FOOTER */}
        <Divider sx={{ my: 4 }} />
        <Box textAlign="center">
          <Typography variant="h5" color="#098963" sx={{ fontWeight: 700, mb: 2 }}>
            🧪 Accurate Diagnostics. Reliable Results. Trusted Care. 💙
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Thank you for choosing MELP Lab | Kerala
          </Typography>
          <Typography sx={{ 
            fontSize: '0.8rem', 
            color: 'text.secondary', 
            mt: 2,
            '@media print': { fontSize: '0.9rem' }
          }}>
            * This is a computer-generated lab invoice
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default LabInvoiceReport;
